/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   ItemInsightsPage.tsx
 *     Feature         :   217-insights-dashboards (T015 / US2, FR-002)
 *     Description     :   Item Insights — cross-type Fabric item fleet dashboard. A type
 *                         filter drives every panel. Replaces the Fabric Items tabbed page.
 *                         Everything renders from collected data (FR-014); missing signals
 *                         say "not collected", never 0.
 *     Author          :   Peter Dobler
 *     Created         :   2026-07-05
 * =========================================================================================
 */

import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { usePageData } from '../../hooks/usePageData';
import { Layers, Info } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { apiClient } from '../../services/apiClient';
import {
  PageLayout, Card, DataTable, Select, StatCard, Badge, AppTooltip, ErrorState, LoadingState, NotCollected,
  Extended, QuickGlance, chartTooltipStyle, Modal, DescriptionList,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';
import { formatEastern, formatEasternDate } from '../../utils/dateFormatter';

interface PipelineDetailRow {
  pipelineId: string;
  name: string;
  pipelineType: string | null;
  state: string | null;
  workspaceName: string | null;
  createdAt: string | null;
  scheduleEnabled: boolean | null;
  scheduleType: string | null;
  scheduleSummary: string | null;
  /** Spec 286 T159 item 31: the worker's schedule read for this pipeline is refused. */
  scheduleReadRefused?: boolean;
  nextRunUtc: string | null;
}

interface ReportTypeBreakdown {
  reportType: string;
  count: number;
  endorsedCount: number;
  boundCount: number;
}

interface EndorsedReportRow {
  name: string;
  reportType: string;
  endorsedStatus: string | null;
  linkedModelName: string | null;
  workspaceName: string | null;
  createdDateTime: string | null;
}

interface ItemInsightsPayload {
  kpis: {
    totalItems: number;
    byType: Array<{ itemType: string; count: number }>;
    createdThisMonth: number;
    modifiedThisWeek: number;
    refreshFailingModelsCount: number;
    /** Spec 286 T159 item 30: models whose latest snapshot carries a refresh status; 0 = not collected. */
    refreshStatusCollectedCount?: number;
    unprotectedItemsCount: number;
  };
  languageMix: Array<{ language: string; count: number }>;
  refreshFailingModels: Array<{ modelId: string; name: string; lastRefreshStatus: string | null; lastRefreshAt: string | null }>;
  rowCountLeaders: Array<{ modelId: string; name: string; rowCount: number }>;
  staleNotebooks: Array<{ notebookId: string; name: string; lastModifiedAt: string | null }>;
  itemCountByTypeTrend: Array<{ date: string; itemType: string; count: number }>;
  pipelines: {
    total: number;
    scheduled: number;
    /** Spec 286 T159 item 31: no schedule on record and the worker's schedule read refused. */
    notReadable?: number;
    unscheduled: number;
    rows: PipelineDetailRow[];
  };
  reports: {
    breakdown: ReportTypeBreakdown[];
    endorsed: EndorsedReportRow[];
  };
}

const ALL_TYPES = '__all__';

export const ItemInsightsPage: React.FC = () => {
  const [typeFilter, setTypeFilter] = useState<string>(ALL_TYPES);
  const [selectedPipeline, setSelectedPipeline] = useState<PipelineDetailRow | null>(null);
  const [selectedReport, setSelectedReport] = useState<EndorsedReportRow | null>(null);

  const query = useQuery<ItemInsightsPayload>({
    queryKey: ['item-insights'],
    queryFn: async () => {
      const res = await apiClient.get<{ success: boolean; data: ItemInsightsPayload }>('/api/item-insights');
      return res.data.data;
    },
    staleTime: 60_000,
  });
  const { data, isLoading, error, refresh, isRefreshing, lastFetchedAt } = usePageData(query);
  // Spec 286 T159 item 30: no model's latest snapshot carries a refresh status, so no failure could be counted.
  const refreshStatusUnknown = data?.kpis.refreshStatusCollectedCount === 0;

  const trendForType = useMemo(() => {
    if (!data) return [];
    const rows = data.itemCountByTypeTrend.filter((r) => typeFilter === ALL_TYPES || r.itemType === typeFilter);
    const byDate = new Map<string, number>();
    for (const r of rows) byDate.set(r.date, (byDate.get(r.date) ?? 0) + r.count);
    return Array.from(byDate.entries()).sort((a, b) => a[0].localeCompare(b[0])).map(([date, count]) => ({ date, count }));
  }, [data, typeFilter]);

  const showModelPanels = typeFilter === ALL_TYPES || typeFilter === 'SemanticModel';
  const showNotebookPanels = typeFilter === ALL_TYPES || typeFilter === 'Notebook';
  const showPipelinePanels = typeFilter === ALL_TYPES || typeFilter === 'Pipeline';
  const showReportPanels = typeFilter === ALL_TYPES || typeFilter === 'Report' || typeFilter === 'PaginatedReport';

  const rowCountColumns: DataTableColumn<ItemInsightsPayload['rowCountLeaders'][number]>[] = [
    { key: 'name', header: 'Semantic Model', width: '60%', cellClassName: 'font-medium text-[var(--color-text-primary)]' },
    { key: 'rowCount', header: 'Row Count', width: '40%', align: 'right', render: (r) => r.rowCount.toLocaleString() },
  ];

  const staleColumns: DataTableColumn<ItemInsightsPayload['staleNotebooks'][number]>[] = [
    { key: 'name', header: 'Notebook', width: '60%', cellClassName: 'font-medium text-[var(--color-text-primary)]' },
    {
      key: 'lastModifiedAt', header: 'Last Modified', width: '40%', align: 'right',
      render: (r) => (r.lastModifiedAt ? formatEasternDate(r.lastModifiedAt) : <NotCollected reason="No modified-date signal collected for this notebook" />),
    },
  ];

  const failingColumns: DataTableColumn<ItemInsightsPayload['refreshFailingModels'][number]>[] = [
    { key: 'name', header: 'Semantic Model', width: '60%', cellClassName: 'font-medium text-[var(--color-text-primary)]' },
    { key: 'lastRefreshAt', header: 'Last Attempt', width: '40%', align: 'right', render: (r) => (r.lastRefreshAt ? formatEastern(r.lastRefreshAt) : '—') },
  ];

  const pipelineColumns: DataTableColumn<PipelineDetailRow>[] = [
    { key: 'name', header: 'Pipeline', width: '34%', cellClassName: 'font-medium text-[var(--color-text-primary)]' },
    { key: 'pipelineType', header: 'Type', width: '15%', render: (r) => r.pipelineType ?? '—' },
    { key: 'state', header: 'State', width: '12%', render: (r) => r.state ?? '—' },
    {
      key: 'scheduleSummary', header: 'Schedule', width: '21%',
      render: (r) =>
        r.scheduleSummary
          ? <span className={r.scheduleEnabled === false ? 'text-[var(--color-text-secondary)]' : 'text-[var(--color-text-primary)]'}>
              {r.scheduleSummary}{r.scheduleEnabled === false ? ' (off)' : ''}
            </span>
          : r.scheduleEnabled === true
            ? <Badge variant="success" isOutlined size="sm">Scheduled</Badge>
            : r.scheduleReadRefused
              ? (
                <AppTooltip title="The collection worker could not read this pipeline's schedule: its identity has no role on the workspace.">
                  <span className="text-[var(--color-text-secondary)]">Not readable</span>
                </AppTooltip>
              )
              : <span className="text-[var(--color-text-secondary)]">None</span>,
    },
    {
      key: 'nextRunUtc', header: 'Next Run', width: '18%', align: 'right',
      render: (r) => (r.nextRunUtc ? formatEastern(r.nextRunUtc) : <span className="text-[var(--color-text-secondary)]">—</span>),
    },
  ];

  const endorsedColumns: DataTableColumn<EndorsedReportRow>[] = [
    { key: 'name', header: 'Report', width: '36%', cellClassName: 'font-medium text-[var(--color-text-primary)]' },
    { key: 'reportType', header: 'Type', width: '20%', render: (r) => r.reportType },
    { key: 'endorsedStatus', header: 'Endorsement', width: '18%', render: (r) => <Badge variant="info" isOutlined size="sm">{r.endorsedStatus ?? '—'}</Badge> },
    { key: 'linkedModelName', header: 'Semantic Model', width: '26%', render: (r) => r.linkedModelName ?? <span className="text-[var(--color-text-secondary)]">—</span> },
  ];

  useAutoRefresh(refresh, { lastFetchedAt });

  return (
    <PageLayout
      title="Fabric Item Insights"
      description="What exists across the tenant, what changed, and what's stale or unrefreshed"
      icon={<Layers className="h-6 w-6" />}
      helpId="insights.item"
      onRefresh={refresh}
      isRefreshing={isRefreshing}
      lastFetchedAt={lastFetchedAt}
      refreshError={error}
      actions={
        <Select
          aria-label="Item type filter"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="w-44"
        >
          <option value={ALL_TYPES}>All types</option>
          {(data?.kpis.byType ?? []).map((t) => (
            <option key={t.itemType} value={t.itemType}>{t.itemType}</option>
          ))}
        </Select>
      }
    >
      {error && !data ? (
        <ErrorState title="Failed to load item insights" message={(error as Error).message} onRetry={refresh} />
      ) : isLoading || !data ? (
        <LoadingState rows={8} />
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
            <div className="relative h-full">
              <StatCard className="h-full" label="Total Items" value={data.kpis.totalItems} />
              <div className="absolute right-3 top-3">
                <AppTooltip title="Total active Fabric items across all six types — lakehouses, warehouses, pipelines, notebooks, semantic models, reports — counted entity-first from dbo.fabric_items_view (is_active).">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
            <div className="relative h-full">
              <StatCard className="h-full" label="Created This Month" value={data.kpis.createdThisMonth} />
              <div className="absolute right-3 top-3">
                <AppTooltip title="Active items first discovered since the 1st of the current month (dbo.fabric_items_view.created_at). Fabric exposes no true creation time, so this reflects when FabricControl first tracked the item.">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
            <div className="relative h-full">
              <StatCard className="h-full" label="Modified This Week" value={data.kpis.modifiedThisWeek} />
              <div className="absolute right-3 top-3">
                <AppTooltip title="Active items whose record changed in the last 7 days (dbo.fabric_items_view.updated_at).">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
            <div className="relative h-full">
              <StatCard
                className="h-full"
                label="Refresh-Failing Models"
                value={refreshStatusUnknown
                  ? <NotCollected reason="No semantic model's latest snapshot carries a refresh status." />
                  : data.kpis.refreshFailingModelsCount}
                valueClassName={data.kpis.refreshFailingModelsCount > 0 ? 'text-[var(--color-status-danger)]' : undefined}
              />
              <div className="absolute right-3 top-3">
                <AppTooltip title="Semantic models whose most recent collected refresh ended in 'Failed' — the latest dbo.semantic_model_snapshots row per model (last_refresh_status).">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
            <div className="relative h-full">
              <StatCard className="h-full" label="Unprotected LH/WH" value={data.kpis.unprotectedItemsCount} valueClassName={data.kpis.unprotectedItemsCount > 0 ? 'text-[var(--color-status-warning)]' : undefined} />
              <div className="absolute right-3 top-3">
                <AppTooltip title="Lakehouses and warehouses with NO backup coverage — neither the item nor its workspace has a backup plan. This is about data protection (backups), NOT refresh health; refresh-failing semantic models are the separate tile to the left.">
                  <span><Info className="h-4 w-4 text-[var(--color-text-secondary)]" /></span>
                </AppTooltip>
              </div>
            </div>
          </div>

          <QuickGlance>
            <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
              Showing the at-a-glance summary. Open this page on a larger screen for type breakdown, trends, and staleness rankings.
            </Card>
          </QuickGlance>

          <Extended>
            <div className="space-y-6">
              {/* Census + cumulative growth, side by side */}
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <Card padding="none">
                  <h2 className="px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">Item census by type</h2>
                  <DataTable
                    columns={[
                      { key: 'itemType', header: 'Type', width: '50%', cellClassName: 'font-medium text-[var(--color-text-primary)]' },
                      { key: 'count', header: 'Count', width: '50%', align: 'right', render: (r) => r.count.toLocaleString() },
                    ] as DataTableColumn<ItemInsightsPayload['kpis']['byType'][number]>[]}
                    rows={data.kpis.byType}
                    rowKey={(r) => r.itemType}
                    showPagination={false}
                    emptyMessage="No items collected"
                  />
                </Card>

                <Card>
                  <div className="mb-1 flex items-center gap-2">
                    <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
                      Cumulative item growth — 60 days {typeFilter !== ALL_TYPES ? `(${typeFilter})` : '(all types)'}
                    </h2>
                    <AppTooltip title="Running total of active items by the date they were first discovered — a cumulative (monotonic) curve showing how the estate has grown, not a point-in-time count.">
                      <span><Info className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" /></span>
                    </AppTooltip>
                  </div>
                  {trendForType.length > 1 ? (
                    <div className="h-56">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={trendForType}>
                          <defs>
                            <linearGradient id="itemGrowthFill" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="var(--color-accent)" stopOpacity={0.35} />
                              <stop offset="100%" stopColor="var(--color-accent)" stopOpacity={0.02} />
                            </linearGradient>
                          </defs>
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} width={45} allowDecimals={false} />
                          <Tooltip {...chartTooltipStyle} />
                          <Area type="monotone" dataKey="count" name="Cumulative items" stroke="var(--color-accent)" strokeWidth={2} fill="url(#itemGrowthFill)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <NotCollected reason="Fewer than 2 days of item history collected in the 60-day window" />
                  )}
                </Card>
              </div>

              {showPipelinePanels && (
                <Card padding="none">
                  <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-4">
                    <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Pipelines</h2>
                    <div className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)]">
                      <Badge variant="secondary" isOutlined size="sm">{data.pipelines.total} total</Badge>
                      <Badge variant="success" isOutlined size="sm">{data.pipelines.scheduled} scheduled</Badge>
                      {(data.pipelines.notReadable ?? 0) > 0 && (
                        <Badge variant="warning" isOutlined size="sm">{data.pipelines.notReadable} schedule not readable</Badge>
                      )}
                      <Badge variant="secondary" isOutlined size="sm">{data.pipelines.unscheduled} unscheduled</Badge>
                    </div>
                  </div>
                  <DataTable columns={pipelineColumns} rows={data.pipelines.rows} rowKey={(r) => r.pipelineId} pageSize={10} emptyMessage="No pipelines collected" onRowClick={(r) => setSelectedPipeline(r)} />
                </Card>
              )}

              {showReportPanels && (
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                  <Card padding="none">
                    <div className="flex items-center gap-2 px-4 pt-4">
                      <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Reports by type</h2>
                      <AppTooltip title="Bound = the report has a linked Power BI semantic model. Paginated reports (RDL) connect directly to data sources and never bind a semantic model, so 0 bound is EXPECTED for them — not a gap.">
                        <span><Info className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" /></span>
                      </AppTooltip>
                    </div>
                    <p className="px-4 text-xs text-[var(--color-text-secondary)]">Endorsed = Promoted/Certified · Bound model = has a linked semantic model (paginated reports have none by design)</p>
                    <DataTable
                      columns={[
                        { key: 'reportType', header: 'Type', width: '34%', cellClassName: 'font-medium text-[var(--color-text-primary)]' },
                        { key: 'count', header: 'Count', width: '22%', align: 'right', render: (r) => r.count.toLocaleString() },
                        { key: 'endorsedCount', header: 'Endorsed', width: '22%', align: 'right', render: (r) => (r.endorsedCount > 0 ? r.endorsedCount : <span className="text-[var(--color-text-secondary)]">0</span>) },
                        { key: 'boundCount', header: 'Bound model', width: '22%', align: 'right', render: (r) => <span className="text-[var(--color-text-secondary)]">{r.boundCount.toLocaleString()} / {r.count.toLocaleString()}</span> },
                      ] as DataTableColumn<ReportTypeBreakdown>[]}
                      rows={data.reports.breakdown}
                      rowKey={(r) => r.reportType}
                      showPagination={false}
                      emptyMessage="No reports collected"
                    />
                  </Card>

                  <Card padding="none">
                    <h2 className="px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">Endorsed reports</h2>
                    <p className="px-4 text-xs text-[var(--color-text-secondary)]">Promoted or Certified reports and their semantic model</p>
                    {data.reports.endorsed.length === 0 ? (
                      <div className="p-4 text-sm text-[var(--color-text-secondary)]">No reports have been promoted or certified.</div>
                    ) : (
                      <DataTable columns={endorsedColumns} rows={data.reports.endorsed} rowKey={(r) => r.name} pageSize={10} emptyMessage="No endorsed reports" onRowClick={(r) => setSelectedReport(r)} />
                    )}
                  </Card>
                </div>
              )}

              {showNotebookPanels && (
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                  <Card padding="none">
                    <h2 className="px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">Notebook language mix</h2>
                    {data.languageMix.length === 0 ? (
                      <div className="p-4"><NotCollected reason="No notebook language signals collected yet (Spec 209 enrichment pass)" /></div>
                    ) : (
                      <DataTable
                        columns={[
                          { key: 'language', header: 'Language', width: '50%', cellClassName: 'font-medium text-[var(--color-text-primary)]' },
                          { key: 'count', header: 'Notebooks', width: '50%', align: 'right' },
                        ] as DataTableColumn<ItemInsightsPayload['languageMix'][number]>[]}
                        rows={data.languageMix}
                        rowKey={(r) => r.language}
                        showPagination={false}
                        emptyMessage="No language signals"
                      />
                    )}
                  </Card>

                  <Card padding="none">
                    <h2 className="px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">Staleness ranking — notebooks</h2>
                    <p className="px-4 text-xs text-[var(--color-text-secondary)]">Never-or-least-recently-modified first</p>
                    <DataTable columns={staleColumns} rows={data.staleNotebooks} rowKey={(r) => r.notebookId} pageSize={10} emptyMessage="No notebooks" />
                  </Card>
                </div>
              )}

              {showModelPanels && (
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                  <Card padding="none">
                    <h2 className="px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">Row-count leaders</h2>
                    {data.rowCountLeaders.length === 0 ? (
                      <div className="p-4"><NotCollected reason="No semantic model row-count signals collected (DirectLake-only environment, or executeQueries not yet run)" /></div>
                    ) : (
                      <DataTable columns={rowCountColumns} rows={data.rowCountLeaders} rowKey={(r) => r.modelId} pageSize={10} emptyMessage="No row-count leaders" />
                    )}
                  </Card>

                  <Card padding="none">
                    <h2 className="px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">Refresh-failing semantic models</h2>
                    <DataTable columns={failingColumns} rows={data.refreshFailingModels} rowKey={(r) => r.modelId} pageSize={10} emptyMessage={refreshStatusUnknown ? 'Refresh status is not collected' : 'No refresh-failing models'} />
                  </Card>
                </div>
              )}
            </div>
          </Extended>
        </>
      )}

      <Modal isOpen={!!selectedPipeline} onClose={() => setSelectedPipeline(null)} title={selectedPipeline?.name} size="md">
        {selectedPipeline && (
          <DescriptionList
            items={[
              { label: 'Type', value: selectedPipeline.pipelineType ?? '—' },
              { label: 'State', value: selectedPipeline.state ?? '—' },
              { label: 'Workspace', value: selectedPipeline.workspaceName ?? '—' },
              { label: 'Schedule', value: selectedPipeline.scheduleSummary ?? (selectedPipeline.scheduleEnabled === true ? 'Scheduled'
                : selectedPipeline.scheduleReadRefused ? 'Not readable: the worker has no role on this workspace' : 'Not scheduled') },
              {
                label: 'Enabled',
                value: selectedPipeline.scheduleEnabled === true
                  ? <Badge variant="success" isOutlined size="sm">On</Badge>
                  : selectedPipeline.scheduleEnabled === false
                    ? <Badge variant="secondary" isOutlined size="sm">Off</Badge>
                    : '—',
              },
              { label: 'Next run', value: selectedPipeline.nextRunUtc ? formatEastern(selectedPipeline.nextRunUtc) : '—' },
              { label: 'Discovered', value: selectedPipeline.createdAt ? formatEastern(selectedPipeline.createdAt) : '—' },
            ]}
          />
        )}
      </Modal>

      <Modal isOpen={!!selectedReport} onClose={() => setSelectedReport(null)} title={selectedReport?.name} size="md">
        {selectedReport && (
          <DescriptionList
            items={[
              { label: 'Type', value: selectedReport.reportType },
              { label: 'Workspace', value: selectedReport.workspaceName ?? '—' },
              { label: 'Endorsement', value: selectedReport.endorsedStatus ? <Badge variant="info" isOutlined size="sm">{selectedReport.endorsedStatus}</Badge> : '—' },
              {
                label: 'Semantic model',
                value: selectedReport.linkedModelName
                  ?? (selectedReport.reportType === 'PaginatedReport' ? 'None (connects directly to data sources)' : 'None'),
              },
              { label: 'Created', value: selectedReport.createdDateTime ? formatEastern(selectedReport.createdDateTime) : '—' },
            ]}
          />
        )}
      </Modal>
    </PageLayout>
  );
};

export default ItemInsightsPage;
